import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetLedgerAccountById, useListPenaltyReviews } from '../../services/loanService';
import type { PenaltyReview } from '../../services/schemas/loanServiceSchemas/penaltyReview';
import { PenaltyReviewStatus } from '../../services/schemas/loanServiceSchemas/penaltyReviewStatus';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/rolePermissions';
import { formatCurrency } from '../../utils/formatters';

/** Pending charges for one loan account, as the business decides on them. */
export interface PendingChargesByAccount {
  accountId: string;
  count: number;
  totalAmount: number;
  oldest?: string;
}

/** Groups PENDING reviews by account, accounts with the most money waiting first. */
export const groupPendingCharges = (reviews: PenaltyReview[] | undefined): PendingChargesByAccount[] => {
  const byAccount = new Map<string, PendingChargesByAccount>();
  for (const review of reviews || []) {
    const accountId = review.accountId || '';
    const group = byAccount.get(accountId) || { accountId, count: 0, totalAmount: 0 };
    group.count++;
    group.totalAmount += review.calculated?.amountDec || 0;
    if (review.createdAt && (!group.oldest || review.createdAt < group.oldest)) {
      group.oldest = review.createdAt;
    }
    byAccount.set(accountId, group);
  }
  return Array.from(byAccount.values()).sort((a, b) => b.totalAmount - a.totalAmount);
};

/**
 * A loan account shown the way staff recognise it: customer name and account number. Reviews
 * only carry the account id, so the account is looked up (react-query caches the lookups).
 */
export const LoanAccountLabel: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { data: account } = useGetLedgerAccountById(accountId, {
    query: { enabled: !!accountId, staleTime: 5 * 60_000 }
  });
  return (
    <span>
      <span className="fw-semibold">{account?.customerName || account?.customerNumber || 'Loan account'}</span>
      <span className="text-muted small d-block">{account?.accountNumber || `${accountId.slice(0, 8)}…`}</span>
    </span>
  );
};

const REVIEW_PAGE_SIZE = 500;
const MAX_LISTED = 6;

/** Remembers, per user and per day, that today's pending-charges reminder has been shown. */
const shownKey = (user: string, day: string) => `tirhelo.pendingChargesReminder.${user}.${day}`;

const wasShown = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const markShown = (key: string) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Storage blocked: the reminder may show again on the next visit, which is harmless.
  }
};

/**
 * Charges waiting for a decision, in the header: the count of loans with pending charges, a list
 * that opens each loan's approve / reject screen, and a reminder the first time a user opens the
 * app on a day with charges waiting.
 */
const PendingChargesAlert: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.MANAGE_PENALTIES);
  const user = useSelector((state: any) => state.auth.user);
  const navigate = useNavigate();

  const { data: reviews } = useListPenaltyReviews(
    { status: PenaltyReviewStatus.PENDING, page: 0, size: REVIEW_PAGE_SIZE },
    { query: { enabled: canManage, staleTime: 60_000, refetchInterval: 5 * 60_000 } }
  );

  const accounts = useMemo(() => groupPendingCharges(reviews?.content), [reviews?.content]);
  const accountCount = accounts.length;
  const totalAmount = accounts.reduce((sum, a) => sum + a.totalAmount, 0);
  const [showReminder, setShowReminder] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!canManage || accountCount === 0) return;
    const key = shownKey(user?.email || user?.name || 'user', today);
    if (!wasShown(key)) {
      markShown(key);
      setShowReminder(true);
    }
  }, [canManage, accountCount, user?.email, user?.name, today]);

  if (!canManage) return null;

  const summary = accountCount
    ? `${accountCount} loan${accountCount === 1 ? '' : 's'} with charges awaiting approval (${formatCurrency(totalAmount)})`
    : 'No charges awaiting approval';

  return (
    <>
      <li>
        <Dropdown align="end">
          <Dropdown.Toggle
            as="a"
            href="#"
            bsPrefix="nav-link"
            className="position-relative px-2 text-secondary"
            title={summary}
            aria-label={summary}
          >
            <i className="fa fa-gavel fs-5"></i>
            {accountCount > 0 && (
              <span
                className="badge rounded-pill bg-danger position-absolute top-0 start-100 translate-middle"
                style={{ fontSize: 11 }}
              >
                {accountCount}
              </span>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu className="border-0 rounded-4 shadow p-0" style={{ width: 340 }}>
            <div className="px-3 py-2 border-bottom">
              <div className="fw-semibold">Pending charges</div>
              <small className="text-muted">{summary}</small>
            </div>

            {accounts.slice(0, MAX_LISTED).map(account => (
              <Dropdown.Item
                key={account.accountId}
                as={Link}
                to={`/collections/penalties/${account.accountId}`}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <LoanAccountLabel accountId={account.accountId} />
                <span className="text-end ms-2">
                  <span className="fw-bold text-danger d-block">{formatCurrency(account.totalAmount)}</span>
                  <small className="text-muted">{account.count} charge{account.count === 1 ? '' : 's'}</small>
                </span>
              </Dropdown.Item>
            ))}

            <Dropdown.Item as={Link} to="/collections/penalties" className="text-center text-primary py-2">
              {accountCount > MAX_LISTED ? `View all ${accountCount} loans` : 'Open pending charges'}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </li>

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1080, position: 'fixed' }}>
        <Toast show={showReminder} onClose={() => setShowReminder(false)} delay={15000} autohide>
          <Toast.Header>
            <i className="fa fa-gavel me-2 text-danger"></i>
            <strong className="me-auto">Charges awaiting approval</strong>
          </Toast.Header>
          <Toast.Body>
            <div className="mb-2">{summary}.</div>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                setShowReminder(false);
                navigate(accountCount === 1 ? `/collections/penalties/${accounts[0].accountId}` : '/collections/penalties');
              }}
            >
              Review charges
            </button>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default PendingChargesAlert;
